/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.slideshow = this.querySelector('.slideshow');
      this.slides = this.querySelectorAll('.slideshow__slide');
      this.nav = this.querySelector('.slideshow-nav');
      this.pagination = this.querySelector('.slideshow-pagination');
      this.rtl = document.dir === 'rtl';
      this.currentIndex = 0;
      this.swipeThreshold = 80;
      this.wheelTriggerWait = 800;
      this.transitionOutDelay = this.dataset.transitionOutDelay || 0;

      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this.nav.addEventListener('click', this.handleNavClick.bind(this));
      }

      if (this.pagination) {
        this.pageBtns = this.querySelectorAll('.page-btn');
        this.pagination.addEventListener('click', this.handlePaginationClick.bind(this));
      }

      if (theme.device.hasTouch) {
        this.slideshow.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.slideshow.addEventListener('touchmove', debounce(this.handleTouchMove.bind(this), 200), { passive: true });
      }

      this.slideshow.addEventListener('wheel', this.handleWheel.bind(this));
      this.slideshow.addEventListener('transitionend', SlideShow.handleTransitionend);

      this.saveSlideVisibility();
      this.setSlideVisibility();
      if (this.dataset.autoplay === 'true') this.initAutoPlay();
    }

    initAutoPlay() {
      this.autoplayEnabled = true;
      this.autoplaySpeed = this.dataset.speed * 1000;
      this.pauseAutoplayOnMouseover = this.dataset.pauseAutoplayOnMouseover !== 'false';

      if (this.pauseAutoplayOnMouseover) {
        this.addEventListener('mouseover', this.pauseAutoplay.bind(this));
        this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
      }

      this.slideshow.addEventListener('focusin', this.pauseAutoplay.bind(this));
      this.slideshow.addEventListener('focusout', this.resumeAutoplay.bind(this));

      this.addObserver();
      this.startAutoplay();
    }

    handleNavClick(evt) {
      if (evt.target.matches('.autoplay-btn')) {
        this.togglePlayState(evt);
        return;
      }
      if (evt.target.name === 'next') {
        this.showNextSlide();
      } else if (evt.target.name === 'prev') {
        this.showPrevSlide();
      }
    }

    handlePaginationClick(evt) {
      if (!evt.target.matches('.page-btn')) return;
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.setActiveSlide(Number(evt.target.dataset.index));
    }

    handleWheel(evt) {
      if (this.blockWheelTrigger) {
        evt.preventDefault();
      } else if (evt.deltaX > 20) {
        evt.preventDefault();
        this.showNextSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      } else if (evt.deltaX < -20) {
        evt.preventDefault();
        this.showPrevSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      }
    }

    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    handleTouchMove(evt) {
      const touchEnd = evt.changedTouches[0].screenX;
      if (touchEnd < this.touchStart - this.swipeThreshold) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      }
      if (touchEnd > this.touchStart + this.swipeThreshold) {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    static handleTransitionend(evt) {
      if (!evt.target.matches('.slideshow__motion-media')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    setActiveSlide(slideIndex, slideDirection) {
      if (slideIndex === this.currentIndex) return;
      this.querySelectorAll('.transition-out').forEach((el) => el.classList.remove('transition-out'));

      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      this.slides[this.currentIndex].classList.add('transition-out');

      setTimeout(() => {
        this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: 'instant' });
        this.slides[slideIndex].classList.add('is-active');
        this.slides[this.currentIndex].classList.remove('is-active', 'transition-out');

        const transitionedEl = this.slides[this.currentIndex].querySelector('.slideshow__motion-media');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        if (this.autoplayEnabled) {
          if (this.autoplayPaused) {
            this.autoplayStartTime = Date.now();
          } else {
            clearTimeout(this.autoplayTimer);
            this.startAutoplay();
          }
        }

        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.setSlideVisibility();
        this.updatePagination();
      }, this.transitionOutDelay);
    }

    updatePagination() {
      if (!this.pagination) return;
      this.pageBtns.forEach((el, index) => {
        if (index === this.currentIndex) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
    }

    startAutoplay() {
      this.autoplayTimeLeft = null;
      this.autoplayStartTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
      this.setSlideshowState('running');
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();
      if (this.autoplayTimeLeft) {
        this.autoplayTimeLeft -= (Date.now() - this.resumedTime);
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed - (Date.now() - this.autoplayStartTime);
      }
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || !this.autoplayPaused) return;
      this.resumedTime = Date.now();
      this.autoplayTimer = setTimeout(() => {
        this.showNextSlide();
        this.startAutoplay();
      }, this.autoplayTimeLeft);
      this.setSlideshowState('running');
    }

    addObserver() {
      if ('IntersectionObserver' in window === false) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      });
      observer.observe(this);
    }

    saveSlideVisibility() {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('a[tabindex]').forEach((link) => {
          link.dataset.dataSlideshowPriorTabindex = link.getAttribute('tabindex');
        });
      });
    }

    setSlideVisibility() {
      this.slides.forEach((el, index) => {
        const links = el.querySelectorAll('a');
        if (index === this.currentIndex) {
          el.setAttribute('aria-hidden', 'false');
          el.removeAttribute('tabindex');
          links.forEach((link) => {
            if (link.dataset.dataSlideshowPriorTabindex !== undefined) {
              link.setAttribute('tabindex', link.dataset.dataSlideshowPriorTabindex);
            } else {
              link.removeAttribute('tabindex');
            }
          });
        } else {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');
          links.forEach((link) => {
            link.setAttribute('tabindex', '-1');
          });
        }
      });
    }

    setSlideshowState(state) {
      this.style.setProperty('--play-state', state);
      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
    }

    togglePlayState(evt) {
      evt.target.classList.toggle('is-paused');
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    }
  }

  customElements.define('slide-show', SlideShow);
}

// ─────────────────────────────────────────────────────────────────
// Product thumbnail controller
// Uses the theme's own MediaGallery.setActiveMedia() API
// ─────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', function () {

  customElements.whenDefined('media-gallery').then(function () {

    // ── Find elements ────────────────────────────────────────────
    const thumbnailsWrapper = document.querySelector('.thumbnails');
    if (!thumbnailsWrapper) return;

    const thumbnailsList = thumbnailsWrapper.querySelector('.slider__grid');
    if (!thumbnailsList) return;

    const thumbnails = Array.from(thumbnailsList.querySelectorAll('li'));
    if (thumbnails.length === 0) return;

    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;

    const mainImageArea = document.querySelector('.main-image');
    if (!mainImageArea) return;

    // ── Config ───────────────────────────────────────────────────
    const visibleCount = 5;
    let startIndex = 0;

    // ── Remove any stale buttons ─────────────────────────────────
    document.querySelectorAll('.thumb-prev, .thumb-next').forEach(function (el) {
      el.remove();
    });

    // ── Create Prev button ───────────────────────────────────────
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'thumb-prev';
    prevBtn.setAttribute('aria-label', 'Previous thumbnails');
    prevBtn.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none"
      stroke="currentColor" stroke-width="2.5"
      stroke-linecap="round" stroke-linejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline></svg>`;

    // ── Create Next button ───────────────────────────────────────
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'thumb-next';
    nextBtn.setAttribute('aria-label', 'Next thumbnails');
    nextBtn.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none"
      stroke="currentColor" stroke-width="2.5"
      stroke-linecap="round" stroke-linejoin="round">
      <polyline points="9 18 15 12 9 6"></polyline></svg>`;

    // ── Insert buttons around thumbnail list ─────────────────────
    const thumbContainer = thumbnailsList.parentNode;
    thumbContainer.style.display    = 'flex';
    thumbContainer.style.alignItems = 'center';
    thumbContainer.style.gap        = '6px';
    thumbContainer.style.width      = '100%';
    thumbContainer.style.overflow   = 'visible';
    thumbContainer.insertBefore(prevBtn, thumbnailsList);
    thumbContainer.appendChild(nextBtn);

    // Make the list flex
    thumbnailsList.style.display       = 'flex';
    thumbnailsList.style.flexDirection = 'row';
    thumbnailsList.style.gap           = '6px';
    thumbnailsList.style.overflow      = 'hidden';
    thumbnailsList.style.flex          = '1';

    // ── Update visible thumbnails ────────────────────────────────
    function updateVisibility() {

      // If total thumbnails <= visibleCount, show all, hide buttons
      if (thumbnails.length <= visibleCount) {
        thumbnails.forEach(function (thumb) {
          thumb.style.display = 'inline-block';
        });
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        return;
      }

      // More than visibleCount — show window of 3, show buttons
      thumbnails.forEach(function (thumb, idx) {
        thumb.style.display =
          (idx >= startIndex && idx < startIndex + visibleCount)
            ? 'inline-block' : 'none';
      });

      prevBtn.style.display = 'flex';
      nextBtn.style.display = 'flex';

      prevBtn.disabled      = startIndex === 0;
      nextBtn.disabled      = startIndex >= thumbnails.length - visibleCount;
      prevBtn.style.opacity = prevBtn.disabled ? '0.3' : '1';
      nextBtn.style.opacity = nextBtn.disabled ? '0.3' : '1';
      prevBtn.style.cursor  = prevBtn.disabled ? 'default' : 'pointer';
      nextBtn.style.cursor  = nextBtn.disabled ? 'default' : 'pointer';
    }

    // ── Highlight active thumbnail ───────────────────────────────
    function setActiveThumbnail(idx) {
      thumbnails.forEach(function (t) { t.classList.remove('is-active'); });
      if (thumbnails[idx]) thumbnails[idx].classList.add('is-active');

      // Only adjust window if navigation buttons are shown
      if (thumbnails.length > visibleCount) {
        if (idx < startIndex) {
          startIndex = idx;
        } else if (idx >= startIndex + visibleCount) {
          startIndex = idx - visibleCount + 1;
        }
      }

      updateVisibility();
    }

    // ── Navigate using theme's own MediaGallery API ──────────────
    function goToMedia(mediaId) {
      mediaGallery.setActiveMedia(mediaId, true);
    }

    // ── Thumbnail click ──────────────────────────────────────────
    thumbnails.forEach(function (thumb, idx) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();

        const mediaId = thumb.dataset.mediaId;
        if (!mediaId) return;

        setActiveThumbnail(idx);
        goToMedia(mediaId);
      });
    });

    // ── Window Prev button ───────────────────────────────────────
    prevBtn.addEventListener('click', function () {
      if (startIndex > 0) {
        startIndex--;
        updateVisibility();
      }
    });

    // ── Window Next button ───────────────────────────────────────
    nextBtn.addEventListener('click', function () {
      if (startIndex < thumbnails.length - visibleCount) {
        startIndex++;
        updateVisibility();
      }
    });

    // ── Sync thumbnail highlight from main slider ────────────────
    function syncThumbnailFromMain() {
      const activeSlide = mainImageArea.querySelector('.slider__item.is-active');
      if (!activeSlide) return;
      const mediaId = activeSlide.dataset.mediaId;
      const idx = thumbnails.findIndex(function (t) {
        return t.dataset.mediaId === mediaId;
      });
      if (idx !== -1) setActiveThumbnail(idx);
    }

    // Listen to theme's carousel select event
    mainImageArea.addEventListener(
      'on:carousel-slider:select',
      debounce(syncThumbnailFromMain, 100)
    );

    // Listen to media gallery change event
    mediaGallery.addEventListener('on:media-gallery:change', function () {
      setTimeout(syncThumbnailFromMain, 100);
    });

    // Listen to variant change
    document.addEventListener('on:variant:change', function () {
      setTimeout(syncThumbnailFromMain, 300);
    });

    // Touch swipe sync on main slider
    const mainSliderTrack = mainImageArea.querySelector('.slider');
    if (mainSliderTrack) {
      let touchStartX = 0;
      mainSliderTrack.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      mainSliderTrack.addEventListener('touchend', function (e) {
        if (Math.abs(touchStartX - e.changedTouches[0].screenX) > 50) {
          setTimeout(syncThumbnailFromMain, 350);
        }
      }, { passive: true });
    }

    // ── Init ─────────────────────────────────────────────────────
    updateVisibility();
    syncThumbnailFromMain();

  }); // end whenDefined

}); // end DOMContentLoaded